import { InteractionType } from '@azure/msal-browser';
import { MsalAuthenticationTemplate } from '@azure/msal-react';
import { LocalizationProvider } from '@mui/lab';
import DateAdapter from '@mui/lab/AdapterDateFns';
import CssBaseline from '@mui/material/CssBaseline';
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
import { FC, ReactNode, useEffect } from 'react';
import ReactGA from 'react-ga4';
import { BrowserRouter as Router, Route, Switch, Redirect } from 'react-router-dom';

import './App.css';
import { PrivateRoute } from './components';
import { Routes } from './constants';
import { HeaderContextHandler, ToastContextProvider, UserContextHandler } from './context';
import { IRoleType } from './models';
import {
  ActionLists,
  BankAccounts,
  CommissionTables,
  Home,
  NetSuiteLog,
  NoAccess,
  NotFound,
  PropertyTypes,
  PropertySubTypes,
  LineOfBusiness,
  MarginCategory,
  FileUseTypes,
  NetsuiteAccounts,
  EmployeeProfileManagementList,
  DynamicFields,
  Events,
  OccupierServices,
  SoftDeletes,
  RepresentedBy,
  SmpBundles,
} from './pages';
import { ActivityTypes } from './pages/activity-types';
import { ImportPeople } from './pages/import/people';
import { ImportProperties } from './pages/import/properties';
import { ImportCompanies } from './pages/import/companies';
import { theme } from './styles';
import { QueryClientProvider, QueryClient } from '@tanstack/react-query';
import { AppInsightsContext } from '@microsoft/applicationinsights-react-js';
import { reactPlugin } from './services';
import { ImportUnitMixes } from './pages/import/unit-mixes';
import { ImportInvestmentSales } from './pages/import/investment-sales';
import { ImportListingStats } from './pages/import/listing-stats';
import { ImportActiveInvestmentSales } from './pages/import/active-investment-sales';
import { ImportFinancials } from './pages/import/financials';
import { SMP, Applications, Users, Licenses, Chargebacks, EmployeeSearch } from './pages/smp';
import { EmployeeSpecialtyTypes } from './pages/employee-specialty-types';
import { ListingInteractionTypes } from './pages/listing-interaction-types';
import { ClientSegmentation } from './pages/company-client-segmentation';
import {SupportTeam} from './pages/support-teams';
import ElasticSearchAdmin from './pages/elastic-search/ElasticSearchAdmin';
import AgentDrawsDetails from './pages/accounting-draws-and-deductions/AgentDrawsDetails';
import AgentDeductionsDetails from './pages/accounting-draws-and-deductions/AgentDeductionsDetails';
import Agents from './pages/accounting-draws-and-deductions/Agents';
import DrawsAndDeductions from './pages/accounting-draws-and-deductions/DrawsAndDeductions';

const { REACT_APP_GA_TRACKING } = require('./buildSettings.json');

declare global {
  interface Window {
    ga: any;
  }
}

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      cacheTime: 0, // no caching of api calls,
      retry: false, // no retry on failed api calls
      retryOnMount: false, // the query will not be retried on mount if it contains an error
      staleTime: Infinity, // no caching of api calls
      refetchOnMount: false, // the query will not refetch on mount.
      refetchOnWindowFocus: false, // the query will not refetch on window focus.
    },
  },
});

const initializeGoogleAnalytics = () => {
  if (!window.ga && REACT_APP_GA_TRACKING) {
    ReactGA.initialize(REACT_APP_GA_TRACKING, {
      gtagOptions: {
        titleCase: false,
      },
    });
  }
};

const AppRoutes: FC = () => {
  return (
    <Switch>
      <PrivateRoute
        exact
        path={Routes.bankAccounts}
        roles={[IRoleType.SystemAdmin]}
        component={BankAccounts}
      />
      <PrivateRoute
        exact
        path={Routes.commissionTables}
        roles={[IRoleType.SystemAdmin, IRoleType.Accounting]}
        component={CommissionTables}
      />
      <PrivateRoute
        exact
        path={Routes.dynamicFields}
        roles={[IRoleType.SystemAdmin, IRoleType.DynamicFields]}
        component={DynamicFields}
      />
      <PrivateRoute
        exact
        path={Routes.employees}
        roles={[IRoleType.SystemAdmin]}
        component={EmployeeProfileManagementList}
      />
      <PrivateRoute
        exact
        path={Routes.employeeSpecialtyTypes}
        roles={[IRoleType.SystemAdmin]}
        component={EmployeeSpecialtyTypes}
      />
      <PrivateRoute
        exact
        path={Routes.fileUseTypes}
        roles={[IRoleType.SystemAdmin]}
        component={FileUseTypes}
      />
      <PrivateRoute
        exact
        path={Routes.netSuiteConfig}
        roles={[IRoleType.SystemAdmin]}
        component={NetsuiteAccounts}
      />
      <PrivateRoute
        exact
        path={Routes.netSuiteLog}
        roles={[IRoleType.Accounting, IRoleType.SystemAdmin]}
        component={NetSuiteLog}
      />
      <PrivateRoute
        exact
        path={Routes.peopleImport}
        roles={[IRoleType.ImportUser, IRoleType.SystemAdmin]}
        component={ImportPeople}
      />
      <PrivateRoute
        exact
        path={Routes.propertiesImport}
        roles={[IRoleType.ImportUser, IRoleType.SystemAdmin]}
        component={ImportProperties}
      />
      <PrivateRoute
        exact
        path={Routes.companiesImport}
        roles={[IRoleType.ImportUser, IRoleType.SystemAdmin]}
        component={ImportCompanies}
      />
      <PrivateRoute
        exact
        path={Routes.unitMixesImport}
        roles={[IRoleType.ImportUser, IRoleType.SystemAdmin]}
        component={ImportUnitMixes}
      />
      <PrivateRoute
        exact
        path={Routes.investmentSalesImport}
        roles={[IRoleType.ImportUser, IRoleType.SystemAdmin]}
        component={ImportInvestmentSales}
      />
      <PrivateRoute
        exact
        path={Routes.activeInvestmentSalesImport}
        roles={[IRoleType.ImportUser, IRoleType.SystemAdmin]}
        component={ImportActiveInvestmentSales}
      />
      <PrivateRoute
        exact
        path={Routes.listingStatsImport}
        roles={[IRoleType.ImportUser, IRoleType.SystemAdmin]}
        component={ImportListingStats}
      />
      <PrivateRoute
        exact
        path={Routes.financialsImport}
        roles={[IRoleType.ImportUser, IRoleType.SystemAdmin]}
        component={ImportFinancials}
      />
      <PrivateRoute
        exact
        path={Routes.lineOfBusiness}
        roles={[IRoleType.SystemAdmin]}
        component={LineOfBusiness}
      />
      <PrivateRoute
        exact
        path={Routes.marginCategory}
        roles={[IRoleType.SystemAdmin]}
        component={MarginCategory}
      />
      <PrivateRoute
        exact
        path={Routes.propertySubTypes}
        roles={[IRoleType.SystemAdmin]}
        component={PropertySubTypes}
      />
      <PrivateRoute
        exact
        path={Routes.propertyTypes}
        roles={[IRoleType.SystemAdmin]}
        component={PropertyTypes}
      />
      <PrivateRoute
        exact
        path={Routes.activityTypes}
        roles={[IRoleType.SystemAdmin]}
        component={ActivityTypes}
      />
      <PrivateRoute
        exact
        path={Routes.listingInteractionTypes}
        roles={[IRoleType.SystemAdmin]}
        component={ListingInteractionTypes}
      />
      <PrivateRoute
        exact
        path={Routes.supportTeam}
        roles={[IRoleType.SystemAdmin]}
        component={SupportTeam}
      />
      <PrivateRoute
        exact
        path={Routes.clientSegmentation}
        roles={[IRoleType.SystemAdmin]}
        component={ClientSegmentation}
      />
      <PrivateRoute
        exact
        path={Routes.events}
        roles={[IRoleType.SystemAdmin, IRoleType.Marketing]}
        component={Events}
      />
      <PrivateRoute
        exact
        path={Routes.occupierServices}
        roles={[IRoleType.SystemAdmin]}
        component={OccupierServices}
      />
      <PrivateRoute
        exact
        path={Routes.softDeletes}
        roles={[IRoleType.SystemAdmin]}
        component={SoftDeletes}
      />
      <PrivateRoute
        exact
        path={Routes.representedBy}
        roles={[IRoleType.SystemAdmin]}
        component={RepresentedBy}
      />
      <PrivateRoute
        path="/draws-and-deductions"
        requiredFeatureFlags={['draws-and-deductions']}
        roles={[IRoleType.SystemAdmin, IRoleType.Accounting]}
      >
        <DrawsAndDeductions>
        <Switch>
          <Route
            exact
            path={'/draws-and-deductions/employees/:employeeId/draws'}
            component={AgentDrawsDetails}
          />
          <Route
            exact
            path={'/draws-and-deductions/employees/:employeeId/deductions'}
            component={AgentDeductionsDetails}
          />
          <Route
            exact
            path={'/draws-and-deductions/employees/:employeeId'}
            render={({ match }) => (
              <Redirect to={`/draws-and-deductions/employees/${(match as any).params.employeeId}/draws`} />
            )}
          />
          <Route
            exact
            path={'/draws-and-deductions/employees'}
            component={Agents}
          />
          <Route exact path="/draws-and-deductions">
            <Redirect to="/draws-and-deductions/employees" />
          </Route>
          <Route path="/draws-and-deductions/*">
            <Redirect to="/draws-and-deductions/employees" />
          </Route>
        </Switch>
          </DrawsAndDeductions>
      </PrivateRoute>

      <PrivateRoute path="/smp" roles={[IRoleType.SystemAdmin]}>
        <SMP>
          <Switch>
            <Route exact path="/smp">
              <Redirect to="/smp/dashboard" />
            </Route>

            <Route path={Routes.viewApplication(':applicationId')} component={Applications} />
            <Route path={Routes.applicationUsers(':applicationId')} component={Users} />
            <Route path={Routes.applicationLicenses(':applicationId')} component={Licenses} />
            <Route path={Routes.applicationChargebacks(':applicationId')} component={Chargebacks} />

            <Route exact path="/smp/dashboard" component={Applications} />
            <Route exact path="/smp/users" component={Users} />
            <Route exact path="/smp/licenses" component={Licenses} />
            <Route exact path="/smp/chargebacks" component={Chargebacks} />
            <PrivateRoute
              exact
              path={Routes.smp + '/employees'}
              roles={[IRoleType.SystemAdmin]}
              component={EmployeeSearch}
            />

            <Route path="/smp/*">
              <Redirect to="/smp/dashboard" />
            </Route>
          </Switch>
        </SMP>
      </PrivateRoute>

      <PrivateRoute
        exact
        path={Routes.elasticSearch}
        roles={[IRoleType.SystemAdmin]}
        component={ElasticSearchAdmin}
      />
      <PrivateRoute
        exact
        path={Routes.smpbundles}
        roles={[IRoleType.SystemAdmin]}
        component={SmpBundles}
      />
      <Route exact path={Routes.home} component={Home} />
      <Route exact path={Routes.actionLists} component={ActionLists} />
      <Route exact path={Routes.noAccess} component={NoAccess} />
      <Route exact component={NotFound} />
    </Switch>
  );
};

// NOTE: Do NOT re-order.
const RouterWithProviders: FC<{ children: ReactNode }> = ({ children }) => (
  <ToastContextProvider>
    <Router>
      <QueryClientProvider client={queryClient}>
        <HeaderContextHandler>
          <UserContextHandler>
            <StyledEngineProvider injectFirst>
              <LocalizationProvider dateAdapter={DateAdapter}>
                <ThemeProvider theme={theme}>
                  <CssBaseline />
                  {children}
                </ThemeProvider>
              </LocalizationProvider>
            </StyledEngineProvider>
          </UserContextHandler>
        </HeaderContextHandler>
      </QueryClientProvider>
    </Router>
  </ToastContextProvider>
);

export default function App() {
  useEffect(() => {
    initializeGoogleAnalytics();
  }, []);

  return (
    <MsalAuthenticationTemplate interactionType={InteractionType.Redirect}>
      <AppInsightsContext.Provider value={reactPlugin}>
        <RouterWithProviders>
          <AppRoutes />
        </RouterWithProviders>
      </AppInsightsContext.Provider>
    </MsalAuthenticationTemplate>
  );
}
